/**
 * useMarketSocket — connects to /api/ws/market and returns live tick + PnL streams.
 * Falls back silently if WS fails; caller can keep polling as a safety net.
 *
 * Emits:
 *   onTicks(list)  — every ~2s
 *   onPnl(snapshot) — every ~6s (only when the caller is authenticated)
 *
 * Auth: relies on the same-origin httpOnly `access_token` cookie that the
 * browser attaches automatically on the WS handshake. No token param needed.
 *
 * Callbacks are stored in refs so parents don't need to wrap them in
 * useCallback to avoid tearing down the WebSocket on every render.
 */
import { useEffect, useRef } from "react";

export function useMarketSocket({ onTicks, onPnl, onBeco }) {
    const wsRef = useRef(null);
    const stopRef = useRef(false);
    const onTicksRef = useRef(onTicks);
    const onPnlRef = useRef(onPnl);
    const onBecoRef = useRef(onBeco);

    // Keep the refs pointing at the latest callbacks without triggering effect reruns.
    useEffect(() => { onTicksRef.current = onTicks; }, [onTicks]);
    useEffect(() => { onPnlRef.current = onPnl; }, [onPnl]);
    useEffect(() => { onBecoRef.current = onBeco; }, [onBeco]);

    useEffect(() => {
        stopRef.current = false;
        const httpBase = process.env.REACT_APP_BACKEND_URL || "";
        const wsBase = httpBase.replace(/^http/, "ws");
        const url = `${wsBase}/api/ws/market`;
        let reconnectTimer = null;

        const connect = () => {
            if (stopRef.current) return;
            let ws;
            try {
                ws = new WebSocket(url);
            } catch {
                return;
            }
            wsRef.current = ws;
            ws.onmessage = (evt) => {
                try {
                    const msg = JSON.parse(evt.data);
                    if (msg.type === "ticks" && onTicksRef.current) onTicksRef.current(msg.data);
                    else if (msg.type === "pnl" && onPnlRef.current) onPnlRef.current(msg.data);
                    else if (msg.type === "beco" && onBecoRef.current) onBecoRef.current(msg.data);
                } catch (err) {
                    if (process.env.NODE_ENV !== "production") console.debug("ws parse:", err);
                }
            };
            ws.onclose = () => {
                if (stopRef.current) return;
                // Reconnect with backoff
                reconnectTimer = setTimeout(connect, 3000);
            };
            ws.onerror = () => {
                try { ws.close(); } catch { /* ignore */ }
            };
        };
        connect();
        return () => {
            stopRef.current = true;
            if (reconnectTimer) clearTimeout(reconnectTimer);
            const ws = wsRef.current;
            if (ws && ws.readyState <= 1) {
                try { ws.close(); } catch { /* ignore */ }
            }
        };
    }, []);
}
